// Смена видимости через проверку — одно правило на курс и статью: хуки доступа
// (`useCourseAccess`, `useArticleAccess`) держат только свою мутацию и свои тексты.
import { toast } from 'sonner'
import type { ValidationError } from '@/types/api'
import type { AccessType } from '@/features/instructions/types'
import { jobTypeLabel } from '@/features/ai/lib/jobTypeLabel'
import type { ModerationState, VisibilityChange } from '../types'
import { parseModerationError, type ModerationFailure } from './parseModerationError'
import { messagesNow } from '@/i18n'
import { messages } from './messages'

export interface VisibilityTexts {
  /** Тост 202: заявка ушла на проверку. */
  review: string
  /** Тост 200 по целевому доступу. */
  done: Record<AccessType, string>
}

interface FailureHandlers {
  refetch: () => void
  /** `429 review_rate_limited` — бизнес-антиспам: отсчёт под селектом, без тоста. */
  startCountdown: (sec: number) => void
  /** Список проблем валидатора (у курса — диалог). Нет обработчика — общий тост. */
  onValidation?: (errors: ValidationError[]) => void
}

const generic = (): string => messagesNow(messages).accessFailed

export const handleVisibilityFailure = (failure: ModerationFailure, h: FailureHandlers): void => {
  switch (failure.kind) {
    case 'validation':
      if (h.onValidation) h.onValidation(failure.errors)
      else toast.error(generic())
      return
    case 'not_allowed':
      toast.error(messagesNow(messages).catalogNotAllowed)
      h.refetch()
      return
    case 'in_progress':
      h.refetch()
      return
    case 'no_changes':
      toast.error(messagesNow(messages).noChanges)
      h.refetch()
      return
    case 'job_busy':
      toast.error(messagesNow(messages).waitForRun(jobTypeLabel(failure.jobType)))
      return
    case 'rate_limited':
      h.startCountdown(failure.retryAfterSec)
      return
    case 'draft_course':
    case 'unknown':
      toast.error(generic())
      return
    default: {
      const unreachable: never = failure
      return unreachable
    }
  }
}

/** Текст успеха. Закрыть то, что ни разу не публиковалось и ждёт проверки, — это отзыв
 *  заявки: «… закрыт(а)» соврал бы, закрывать было нечего. */
export const visibilityDoneText = (
  texts: VisibilityTexts,
  res: VisibilityChange,
  next: AccessType,
  withdrawing: boolean,
): string => {
  if (res.kind === 'review') return texts.review
  return next === 'private' && withdrawing
    ? messagesNow(messages).requestWithdrawn
    : texts.done[next]
}

interface ApplyArgs extends FailureHandlers {
  state: ModerationState | undefined
  next: AccessType
  texts: VisibilityTexts
  request: () => Promise<VisibilityChange>
}

/** Запрос смены видимости с тостом успеха или разбором отказа. */
export const applyVisibility = async ({
  state,
  next,
  texts,
  request,
  ...handlers
}: ApplyArgs): Promise<void> => {
  // Снимок ДО запроса: ответ инвалидирует кеш, и ревью уже не будет `pending`.
  const withdrawing = state?.review?.status === 'pending' && state.visibility === 'unpublished'
  try {
    const res = await request()
    toast.success(visibilityDoneText(texts, res, next, withdrawing))
  } catch (err) {
    handleVisibilityFailure(parseModerationError(err), handlers)
  }
}
